import QuestionLabel from './QuestionLabel';
import { formatCurrency, roundCurrency } from '~/lib/CurrencyHelpers';
import { useInvoiceContext } from '~/lib/InvoiceContext';

const InvoiceSummaryQuestion = ({ label, value, setValue, settings, isReadOnly }) => {
    const adjustments = settings?.adjustments || [];
    const invoiceContext = useInvoiceContext();
    const allQuestions = invoiceContext?.questions || [];

    // Calculate subtotal from all line items in the invoice
    const calculateSubtotal = () => {
        let subtotal = 0;

        // Calculate from line items in all questions
        allQuestions.forEach(question => {
            if (question.type === 'invoicelineitems') {
                const lineItems = question.settings?.lineItems || [];
                lineItems.forEach(item => {
                    const quantity = item.quantity || 0;
                    const unitPrice = item.unitPrice || 0;
                    subtotal += quantity * unitPrice;
                });
            }
        });

        // Rounded to cents to match Invoice::getTotalAttribute, which is what gets charged
        return roundCurrency(subtotal);
    };

    const calculateAdjustmentAmount = (adjustment, subtotal) => {
        if (adjustment.type === 'percentage') {
            return roundCurrency((subtotal * (adjustment.value || 0)) / 100);
        }
        return roundCurrency(adjustment.value || 0);
    };

    const calculateTotal = () => {
        const subtotal = calculateSubtotal();
        const adjustmentsTotal = adjustments.reduce((sum, adj) => {
            const amount = calculateAdjustmentAmount(adj, subtotal);
            return sum + (adj.operation === 'subtract' ? -amount : amount);
        }, 0);
        return roundCurrency(subtotal + adjustmentsTotal);
    };

    const subtotal = calculateSubtotal();
    const total = calculateTotal();

    return (
        <div className="space-y-4 flex justify-end">
            <div className="rounded-xl px-6 py-4 bg-gray-200 bg-opacity-50 min-w-64 w-full md:w-auto">
                {/* Subtotal */}
                <div className="flex justify-between items-center py-1.5  gap-x-6 text-gray-700">
                    <span>Subtotal</span>
                    <span>{formatCurrency(subtotal)}</span>
                </div>

                {/* Adjustments */}
                {adjustments.length > 0 && (
                    <div className="">
                        {adjustments.map((adjustment, index) => {
                            const amount = calculateAdjustmentAmount(adjustment, subtotal);
                            const displayAmount = adjustment.operation === 'subtract' ? -amount : amount;

                            return (
                                <div
                                    key={index}
                                    className="flex justify-between items-center gap-x-6 py-1.5 text-gray-600"
                                >
                                    <span>
                                        {adjustment.name}
                                        {adjustment.type === 'percentage' && (
                                            <span className="text-sm text-gray-500 ml-1">
                                                {adjustment.operation === 'subtract' ? '-' : '+'}
                                                {adjustment.value}%
                                            </span>
                                        )}
                                    </span>
                                    <span>
                                        {displayAmount < 0 ? '-' : '+'}
                                        {formatCurrency(Math.abs(displayAmount))}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Total */}
                <div className="border-t border-gray-300 border-opacity-75 font-bold mt-2 py-1.5 flex justify-between items-center">
                    <span className="">Total</span>
                    <span className="">
                        {formatCurrency(total)}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default InvoiceSummaryQuestion;

